/**
 * Definição dos tutoriais guiados. Só dados e funções puras: o componente
 * converte isto em passos do Joyride. Conteúdo em texto simples (sem JSX) para
 * o módulo continuar testável no Vitest, que roda em ambiente `node`.
 */

export type TourKey = 'dashboard' | 'available-spots' | 'my-parking-spots';

export interface TourStepDefinition {
  /** Seletor CSS do elemento destacado. */
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto';
}

interface BuildOptions {
  /** No desktop a navegação é a sidebar; no mobile, a barra de abas de baixo. */
  isMobile: boolean;
}

const ROUTE_TOURS: Array<{ path: string; tourKey: TourKey }> = [
  { path: '/dashboard', tourKey: 'dashboard' },
  { path: '/available-spots', tourKey: 'available-spots' },
  { path: '/my-parking-spots', tourKey: 'my-parking-spots' },
];

export function resolveTourKeyForPath(pathname: string): TourKey | null {
  return ROUTE_TOURS.find((route) => pathname.startsWith(route.path))?.tourKey ?? null;
}

function dashboardSteps({ isMobile }: BuildOptions): TourStepDefinition[] {
  const navigation: TourStepDefinition = isMobile
    ? {
        target: '[data-tour="mobile-tab-bar"]',
        title: 'Sua navegação',
        content:
          'Por aqui você alterna entre Início, Vagas disponíveis, Minhas vagas e Histórico.',
        placement: 'top',
      }
    : {
        target: '[data-tour="sidebar-nav"]',
        title: 'Sua navegação',
        content: 'O menu lateral leva para as vagas do condomínio, suas vagas e o histórico.',
        placement: 'right',
      };

  return [
    navigation,
    {
      target: '[data-tour="dashboard-tabs"]',
      title: 'Ranking e estatísticas',
      content:
        'Esta tela tem duas seções: o placar do condomínio e os números do seu uso do app.',
    },
    {
      target: '[data-tour="dashboard-ranking"]',
      title: 'Placar do condomínio',
      content:
        'Mostra quem mais disponibilizou e quem mais emprestou vagas. Troque o período para ver a semana, o mês ou tudo.',
    },
    {
      target: '[data-tour="topbar-notifications"]',
      title: 'Notificações',
      content:
        'Aqui chegam os pedidos da sua vaga e as respostas aos seus pedidos. Ative o push para receber no celular.',
    },
    {
      target: '[data-tour="topbar-user-menu"]',
      title: 'Seu menu',
      content: 'Perfil, tema do app e a opção de ver este tutorial ficam neste menu.',
    },
  ];
}

function availableSpotsSteps(): TourStepDefinition[] {
  return [
    {
      target: '[data-tour="spots-tabs"]',
      title: 'Disponíveis e ocupadas',
      content:
        'Em Disponíveis ficam as vagas que vizinhos liberaram. Em Ocupadas você vê o resto do condomínio e pode pedir mesmo assim.',
    },
    {
      target: '[data-tour="available-spots-filters"]',
      title: 'Período da busca',
      content:
        'A lista mostra apenas vagas com janela dentro do período filtrado, que começa em 90 dias. Se uma vaga desaparecer, confira este filtro primeiro.',
    },
    {
      target: '[data-tour="spot-card"]',
      title: 'A vaga',
      content: 'Cada card traz o código, o bloco e o período em que a vaga está livre.',
    },
    {
      target: '[data-tour="spot-request-button"]',
      title: 'Pedir a vaga',
      content:
        'Ao solicitar, o dono recebe uma notificação e decide. Você é avisado da resposta.',
    },
    {
      target: '[data-tour="bulk-request-promo"]',
      title: 'Não achou nada livre?',
      content:
        'A solicitação em massa avisa todos os donos de uma vez, e quem puder liberar responde.',
    },
  ];
}

function myParkingSpotsSteps(): TourStepDefinition[] {
  return [
    {
      target: '[data-tour="my-spots-tabs"]',
      title: 'Suas vagas',
      content: 'Em Minhas ficam as vagas que você possui. Em Usando, as que pegou emprestado.',
    },
    {
      target: '[data-tour="open-availability-drawer"]',
      title: 'Disponibilizar a vaga',
      content:
        'Escolha o período em que sua vaga fica livre. Quem precisar dela no intervalo vai poder solicitar.',
    },
    {
      target: '[data-tour="active-windows"]',
      title: 'Períodos publicados',
      content:
        'Em Janelas ativas você acompanha os períodos já publicados e pode editar ou cancelar cada um.',
    },
  ];
}

export function buildTourSteps(tourKey: TourKey, options: BuildOptions): TourStepDefinition[] {
  switch (tourKey) {
    case 'dashboard':
      return dashboardSteps(options);
    case 'available-spots':
      return availableSpotsSteps();
    case 'my-parking-spots':
      return myParkingSpotsSteps();
  }
}

/**
 * Passos cujo alvo não está na tela (lista vazia, elemento só do desktop) são
 * descartados: melhor um tour curto do que um passo apontando para o nada.
 */
export function filterRenderedSteps(
  steps: TourStepDefinition[],
  exists: (target: string) => boolean,
): TourStepDefinition[] {
  return steps.filter((step) => exists(step.target));
}

/** Menos de dois passos não justifica abrir o overlay do tutorial. */
export const MIN_STEPS_TO_RUN = 2;

interface SelectTourInput {
  pathname: string;
  isMobile: boolean;
  exists: (target: string) => boolean;
}

export interface SelectedTour {
  tourKey: TourKey;
  steps: TourStepDefinition[];
}

export function selectTour({
  pathname,
  isMobile,
  exists,
}: SelectTourInput): SelectedTour | null {
  const tourKey = resolveTourKeyForPath(pathname);
  if (!tourKey) return null;

  const steps = filterRenderedSteps(buildTourSteps(tourKey, { isMobile }), exists);
  if (steps.length < MIN_STEPS_TO_RUN) return null;

  return { tourKey, steps };
}
